import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import type { KeyboardEvent } from 'react';
import { useParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  User, LogIn, LogOut, Bell, BellOff,
  RefreshCw,
} from 'lucide-react';
import { useJukeboxCliente } from '../contexts/JukeboxClienteContext';
import { formatPhoneBrMask } from '@/shared/lib/phoneUtils';
import { atualizarPerfil, buscarPerfilCompleto, syncWhatsappProfile } from '../../data/jukeboxClienteService';
import type { PerfilCompleto } from '../../domain/entities/PerfilJukebox';
import { calcularRankFa } from '../../domain/usecases/calcularRankFa';
import { useCountUp } from '../hooks/useCountUp';
import { usePerfilNovidades } from '../hooks/usePerfilNovidades';
import { PerfilHeroCard } from '../components/PerfilHeroCard';
import { PerfilHeroStats } from '../components/PerfilHeroStats';
import { ConquistaDesbloqueada } from '../components/ConquistaDesbloqueada';
import { CONQUISTA_ICON_MAP } from '../components/conquistaIcons';

const ALL_BADGES: { id: string; nome: string; icone: string }[] = [
  { id: 'estreante', nome: 'Estreante', icone: 'Star' },
  { id: 'fa_dedicado', nome: 'Fã Dedicado', icone: 'Heart' },
  { id: 'lenda', nome: 'Lenda', icone: 'Crown' },
  { id: 'frequentador', nome: 'Frequentador', icone: 'Calendar' },
  { id: 'hit_machine', nome: 'Hit Machine', icone: 'Disc' },
  { id: 'ecletico', nome: 'Eclético', icone: 'Shuffle' },
  { id: 'romantico', nome: 'Romântico', icone: 'HeartPink' },
  { id: 'sofredor', nome: 'Sofredor', icone: 'CloudRain' },
];

function chaveAnimacaoSessao(clienteId: string): string {
  return `ds:jukebox_perfil_sessao_animada:${clienteId}`;
}

function deveAnimarHeroSessao(clienteId: string | undefined): boolean {
  if (!clienteId) return false;
  try {
    if (sessionStorage.getItem(chaveAnimacaoSessao(clienteId))) return false;
    sessionStorage.setItem(chaveAnimacaoSessao(clienteId), '1');
    return true;
  } catch {
    return true;
  }
}

export default function JukeboxPerfilPage() {
  const { telaId } = useParams<{ telaId: string }>();
  const { status, cliente, openLogin, logout, onLoginSuccess } = useJukeboxCliente();
  const [perfil, setPerfil] = useState<PerfilCompleto | null>(null);
  const [loading, setLoading] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const [notifToggling, setNotifToggling] = useState(false);
  const [syncingWhatsapp, setSyncingWhatsapp] = useState(false);
  const [avatarBroken, setAvatarBroken] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const animarHeroRef = useRef<boolean | null>(null);

  if (animarHeroRef.current === null && cliente?.id) {
    animarHeroRef.current = deveAnimarHeroSessao(cliente.id);
  }
  const animarHero = animarHeroRef.current ?? false;

  const { novoNivel, novasConquistas, marcarComoVisto } = usePerfilNovidades(cliente?.id, perfil);

  useEffect(() => {
    setAvatarBroken(false);
  }, [cliente?.avatar_url]);

  useEffect(() => {
    if (!cliente) return;
    setLoading(true);
    buscarPerfilCompleto(cliente.token)
      .then(data => { if (data) setPerfil(data); })
      .finally(() => setLoading(false));
  }, [cliente]);

  useEffect(() => {
    if (editingName) nameInputRef.current?.focus();
  }, [editingName]);

  const startEditName = () => {
    setNameValue(cliente?.nome || '');
    setEditingName(true);
  };

  const saveName = useCallback(async () => {
    if (!cliente) return;
    const trimmed = nameValue.trim();
    setEditingName(false);
    if (!trimmed || trimmed === cliente.nome) return;

    await atualizarPerfil(cliente.token, { nome: trimmed });

    onLoginSuccess({ ...cliente, nome: trimmed });
  }, [cliente, nameValue, onLoginSuccess]);

  const handleNameKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') void saveName();
  };

  const toggleNotificacoes = useCallback(async () => {
    if (!cliente || !perfil) return;
    const newVal = !perfil.notificacoes_ativo;
    setNotifToggling(true);
    setPerfil(prev => prev ? { ...prev, notificacoes_ativo: newVal } : prev);

    await atualizarPerfil(cliente.token, { notificacoes: newVal });

    setNotifToggling(false);
  }, [cliente, perfil]);

  const atualizarPerfilWhatsapp = useCallback(async () => {
    if (!cliente || !telaId || syncingWhatsapp) return;
    setSyncingWhatsapp(true);
    try {
      const atualizado = await syncWhatsappProfile(telaId, cliente);
      if (atualizado) onLoginSuccess(atualizado);
    } finally {
      setSyncingWhatsapp(false);
    }
  }, [cliente, telaId, syncingWhatsapp, onLoginSuccess]);

  const rank = useMemo(() => calcularRankFa(perfil?.total_pedidos ?? 0), [perfil?.total_pedidos]);
  const progressoAnimado = useCountUp(rank.progressoPct, 700);
  const progressoExibido = animarHero ? progressoAnimado : rank.progressoPct;
  const topThumbnail = perfil?.top_musicas[0]?.thumbnail ?? null;

  if (status === 'anonymous') {
    return (
      <div className="min-h-dvh bg-jukebox-bg pt-[env(safe-area-inset-top)] pb-24 px-4 flex items-center justify-center">
        <div className="text-center space-y-4 max-w-xs">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-orange-500/10 flex items-center justify-center">
            <User size={28} className="text-orange-400" />
          </div>
          <h2 className="text-lg font-bold text-white">Seu Perfil</h2>
          <p className="text-sm text-white/50">
            Entre com seu WhatsApp para salvar seu histórico, receber notificações e ter limite ampliado de pedidos.
          </p>
          <button
            onClick={openLogin}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-orange-500 to-rose-500 text-white font-semibold"
          >
            <LogIn size={18} />
            Entrar com WhatsApp
          </button>
        </div>
      </div>
    );
  }

  const phoneDisplay = cliente?.phone_e164
    ? formatPhoneBrMask(cliente.phone_e164.replace(/^55/, ''))
    : '';

  const unlockedIds = new Set(perfil?.conquistas.map(c => c.id) ?? []);
  const vibeTotal = perfil ? perfil.vibes.romance + perfil.vibes.sofrencia + perfil.vibes.neutro : 0;

  return (
    <div className="min-h-dvh bg-jukebox-bg pt-[env(safe-area-inset-top)] pb-24">
      <div className="sticky top-0 z-20 bg-jukebox-bg/90 backdrop-blur-xl border-b border-white/[0.05] px-4 py-3">
        <h1 className="text-base font-bold text-white">Perfil</h1>
      </div>

      <AnimatePresence>
        {(novoNivel || novasConquistas.length > 0) && (
          <ConquistaDesbloqueada
            key="conquista-toast"
            novoNivel={novoNivel}
            novasConquistas={novasConquistas}
            onDismiss={marcarComoVisto}
          />
        )}
      </AnimatePresence>

      {loading ? (
        <div className="px-4 pt-6 space-y-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-20 rounded-2xl bg-white/[0.03] animate-pulse" />
          ))}
        </div>
      ) : (
        <motion.div
          className="px-4 pt-6 space-y-6"
          initial={animarHero ? { opacity: 0, y: 12 } : false}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
        >
          {/* Hero Card + Stats */}
          <div className="relative">
            <div className="absolute -top-10 left-1/2 -translate-x-1/2 w-3/4 h-40 bg-orange-500/10 blur-3xl pointer-events-none" aria-hidden />
            <div className="relative space-y-4">
              <PerfilHeroCard
                nome={cliente?.nome ?? null}
                phoneDisplay={phoneDisplay}
                avatarUrl={cliente?.avatar_url ?? null}
                avatarBroken={avatarBroken}
                onAvatarError={() => setAvatarBroken(true)}
                membroDesde={perfil?.membro_desde ?? null}
                rank={rank}
                topThumbnail={topThumbnail}
                progressoPctAnimado={progressoExibido}
                edit={{
                  editing: editingName,
                  value: nameValue,
                  inputRef: nameInputRef,
                  onChange: setNameValue,
                  onKeyDown: handleNameKeyDown,
                  onSave: () => void saveName(),
                  onStart: startEditName,
                }}
              />

              {perfil && (
                <PerfilHeroStats
                  totalPedidos={perfil.total_pedidos}
                  totalTocadas={perfil.total_tocadas}
                  taxaSucesso={perfil.taxa_sucesso}
                  diasAtivos={perfil.dias_ativos}
                  animar={animarHero}
                />
              )}
            </div>
          </div>

          {/* Conquistas */}
          {perfil && (
            <div className="space-y-2">
              <p className="text-[11px] font-medium text-white/50">Conquistas</p>
              <div className="scrollbar-hide -mx-1 flex gap-2 overflow-x-auto px-1 pb-2">
                {ALL_BADGES.map(badge => {
                  const unlocked = unlockedIds.has(badge.id);
                  const IconComp = CONQUISTA_ICON_MAP[badge.icone] || CONQUISTA_ICON_MAP.Star;
                  return (
                    <motion.div
                      key={badge.id}
                      whileTap={{ scale: 0.94 }}
                      className={`flex w-16 shrink-0 flex-col items-center gap-1.5 rounded-xl border py-2.5 transition-colors ${
                        unlocked
                          ? 'border-orange-500/20 bg-orange-500/[0.08]'
                          : 'border-white/[0.05] bg-white/[0.02] opacity-40'
                      }`}
                    >
                      <IconComp
                        size={18}
                        className={unlocked ? 'text-orange-400' : 'text-white/30'}
                        fill={unlocked && (badge.id === 'romantico') ? 'currentColor' : 'none'}
                      />
                      <span className={`text-[10px] font-medium text-center leading-tight ${
                        unlocked ? 'text-white/70' : 'text-white/30'
                      }`}>
                        {badge.nome}
                      </span>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Top músicas */}
          {perfil && perfil.top_musicas.length > 0 && (
            <div className="space-y-2">
              <p className="text-[11px] font-medium text-white/50">Top músicas</p>
              <div className="divide-y divide-white/[0.05] overflow-hidden rounded-2xl border border-white/[0.06] bg-white/[0.02]">
                {perfil.top_musicas.map((m, i) => (
                  <motion.div
                    key={i}
                    whileTap={{ scale: 0.98 }}
                    className="flex items-center gap-3 px-3 py-3"
                  >
                    <span className="text-xs font-bold text-white/50 w-4 text-center">{i + 1}</span>
                    <img src={m.thumbnail} alt="" loading="lazy" className="w-10 h-10 rounded-lg object-cover" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate">{m.titulo}</p>
                    </div>
                    <span className="text-xs text-white/55 shrink-0">{m.vezes}x</span>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {/* Top artistas */}
          {perfil && perfil.top_artistas.length > 0 && (
            <div className="space-y-2">
              <p className="text-[11px] font-medium text-white/50">Seus artistas</p>
              <div className="flex gap-3">
                {perfil.top_artistas.map((a, i) => (
                  <div key={i} className="flex-1 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-3">
                    <p className="truncate text-sm font-medium text-white">{a.artista}</p>
                    <p className="text-xs text-white/55">{a.vezes} pedidos</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Sua vibe */}
          {perfil && vibeTotal > 0 && (
            <div className="space-y-2">
              <p className="text-[11px] font-medium text-white/50">Sua vibe</p>
              <div className="space-y-3 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4">
                <div className="flex h-3 overflow-hidden rounded-full">
                  {perfil.vibes.romance > 0 && (
                    <div
                      className="bg-rose-500 transition-all"
                      style={{ width: `${(perfil.vibes.romance / vibeTotal) * 100}%` }}
                    />
                  )}
                  {perfil.vibes.sofrencia > 0 && (
                    <div
                      className="bg-cyan-500 transition-all"
                      style={{ width: `${(perfil.vibes.sofrencia / vibeTotal) * 100}%` }}
                    />
                  )}
                  {perfil.vibes.neutro > 0 && (
                    <div
                      className="bg-white/20 transition-all"
                      style={{ width: `${(perfil.vibes.neutro / vibeTotal) * 100}%` }}
                    />
                  )}
                </div>
                <div className="flex justify-between text-[11px]">
                  <span className="text-rose-400">Romance {Math.round((perfil.vibes.romance / vibeTotal) * 100)}%</span>
                  <span className="text-cyan-400">Sofrência {Math.round((perfil.vibes.sofrencia / vibeTotal) * 100)}%</span>
                  <span className="text-white/40">Neutro {Math.round((perfil.vibes.neutro / vibeTotal) * 100)}%</span>
                </div>
              </div>
            </div>
          )}

          {/* Configurações */}
          <div className="space-y-3 border-t border-white/[0.05] pt-2">
            <p className="text-[11px] font-medium text-white/50">Configurações</p>

            <motion.button
              type="button"
              whileTap={{ scale: 0.96 }}
              onClick={() => void atualizarPerfilWhatsapp()}
              disabled={syncingWhatsapp}
              className="flex w-full items-center justify-between rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4 disabled:opacity-50"
            >
              <div className="flex items-center gap-3">
                <RefreshCw
                  size={18}
                  className={`text-orange-400 ${syncingWhatsapp ? 'animate-spin' : ''}`}
                />
                <div className="text-left">
                  <span className="block text-sm font-medium text-white">Atualizar do WhatsApp</span>
                  <span className="text-[11px] text-white/55">Foto e nome exibido no app</span>
                </div>
              </div>
            </motion.button>

            <motion.button
              whileTap={{ scale: 0.96 }}
              onClick={() => void toggleNotificacoes()}
              disabled={notifToggling}
              className="flex items-center justify-between w-full p-4 rounded-2xl bg-white/[0.03] border border-white/[0.05]"
            >
              <div className="flex items-center gap-3">
                {perfil?.notificacoes_ativo ? (
                  <Bell size={18} className="text-emerald-400" />
                ) : (
                  <BellOff size={18} className="text-white/40" />
                )}
                <div className="text-left">
                  <span className="text-sm font-medium text-white block">Notificações WhatsApp</span>
                  <span className="text-[11px] text-white/55">Responda PARAR para cancelar</span>
                </div>
              </div>
              <div className={`w-10 h-6 rounded-full relative transition-colors ${
                perfil?.notificacoes_ativo ? 'bg-emerald-500' : 'bg-white/10'
              }`}>
                <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                  perfil?.notificacoes_ativo ? 'translate-x-5' : 'translate-x-1'
                }`} />
              </div>
            </motion.button>

            <motion.button
              whileTap={{ scale: 0.96 }}
              onClick={logout}
              className="flex items-center gap-3 w-full p-4 rounded-2xl bg-white/[0.03] border border-white/[0.05] text-white/60 hover:text-white/80 transition-colors"
            >
              <LogOut size={18} />
              <span className="text-sm font-medium">Sair</span>
            </motion.button>
          </div>
        </motion.div>
      )}
    </div>
  );
}
